import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  VideoCall as VideoCallIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Smartphone as SmartphoneIcon,
  Computer as ComputerIcon,
  Tv as TvIcon,
} from '@mui/icons-material';

const steps = ['Select Time Slot', 'Payment Details', 'Confirmation'];

const BookConfirm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const expert = location.state?.expert;
  const deviceType = location.state?.deviceType;
  const issueDescription = location.state?.issueDescription;

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM',
    '02:00 PM', '03:00 PM', '04:00 PM'
  ];

  const handleNext = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setActiveStep((prev) => prev + 1);
      setLoading(false);
    }, 1500);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const getSpecialtyIcon = (type) => {
    switch(type) {
      case 'phone':
        return <SmartphoneIcon />;
      case 'computer':
        return <ComputerIcon />;
      case 'tv':
        return <TvIcon />;
      default:
        return null;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Available Time Slots
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 2 
            }}>
              {timeSlots.map((slot) => (
                <Button
                  key={slot}
                  variant={selectedSlot === slot ? "contained" : "outlined"}
                  onClick={() => setSelectedSlot(slot)}
                  startIcon={<ScheduleIcon />}
                  sx={{
                    borderColor: '#FFE302',
                    color: selectedSlot === slot ? 'black' : '#FFE302',
                    bgcolor: selectedSlot === slot ? '#FFE302' : 'transparent',
                    '&:hover': {
                      bgcolor: selectedSlot === slot ? '#e6cc00' : 'rgba(255,227,2,0.1)'
                    }
                  }}
                >
                  {slot}
                </Button>
              ))}
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Payment Details
            </Typography>
            {/* Add your payment form here */}
            <Alert severity="info">
              Payment integration will be implemented here
            </Alert>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: '#FFE302', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              Booking Confirmed!
            </Typography>
            <Typography color="text.secondary">
              Your consultation is scheduled for {selectedSlot}
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      bgcolor: '#000', 
      minHeight: '100vh', 
      color: 'white',
      pt: 8,
      pb: 6 
    }}>
      <Container maxWidth="md">
        <Paper sx={{ 
          p: 4, 
          bgcolor: '#111',
          border: '1px solid rgba(255,227,2,0.1)'
        }}>
          <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
            Book Your Consultation
          </Typography>

          {expert && (
            <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(255,227,2,0.1)', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Consultation Details:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getSpecialtyIcon(expert.specialtyType)}
                <Typography>{expert.specialty}</Typography>
              </Box>
            </Box>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: 'white',
                    },
                    '& .MuiStepIcon-root': {
                      color: '#FFE302',
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                sx={{ color: '#999' }}
              >
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || (activeStep === 0 && !selectedSlot)}
                startIcon={loading ? <CircularProgress size={20} /> : <VideoCallIcon />}
                sx={{
                  bgcolor: '#FFE302',
                  color: 'black',
                  '&:hover': {
                    bgcolor: '#e6cc00'
                  }
                }}
              >
                {loading ? 'Processing...' : 'Continue'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate('/consultation')}
                sx={{
                  bgcolor: '#FFE302',
                  color: 'black',
                  '&:hover': {
                    bgcolor: '#e6cc00'
                  }
                }}
              >
                Done
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default BookConfirm; 